import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickStartGuide = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      id: 0,
      title: "Get Your API Key",
      description: "Sign up for a developer account and generate your API credentials from the dashboard.",
      icon: "Key",
      code: `// Add to your .env file
POLICY_ALERTS_API_KEY=pa_live_abc123xyz789
POLICY_ALERTS_ENVIRONMENT=production`,
      language: "bash"
    },
    {
      id: 1,
      title: "Install SDK",
      description: "Install our official SDK for your preferred programming language.",
      icon: "Download",
      code: `# Node.js / JavaScript
npm install @policyalerts/sdk

# Python
pip install policyalerts

# Ruby
gem install policyalerts

# Java
maven dependency: com.policyalerts:sdk:2.0.0`,
      language: "bash"
    },
    {
      id: 2,
      title: "Initialize Client",
      description: "Configure the SDK client with your API credentials and environment settings.",
      icon: "Settings",
      code: `// JavaScript/Node.js
const PolicyAlerts = require('@policyalerts/sdk');

const client = new PolicyAlerts.Client({
  apiKey: process.env.POLICY_ALERTS_API_KEY,
  environment: 'production',
  timeout: 30000
});

// Verify connection
const status = await client.health.check();
console.log('API Status:', status);`,
      language: "javascript"
    },
    {
      id: 3,
      title: "Make Your First Request",
      description: "Fetch policy data and start monitoring medical policy changes across all payers.",
      icon: "Zap",
      code: `// Fetch recent policy updates
const policies = await client.policies.list({
  category: 'cardiovascular',
  status: 'active',
  limit: 10,
  sort: '-updated_at'
});

policies.forEach(policy => {
  console.log(\`\${policy.title} - \${policy.payer}\`);
  console.log(\`Last Updated: \${policy.updated_at}\`);
});`,
      language: "javascript"
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Quick Start Guide
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Get up and running with PolicyAlerts API in minutes. Follow these simple steps to integrate policy monitoring into your application.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="space-y-4">
            {steps?.map((step, index) => (
              <div
                key={step?.id}
                onClick={() => setActiveStep(index)}
                className={`p-4 md:p-6 rounded-lg border-2 cursor-pointer transition-all ${
                  activeStep === index
                    ? 'border-blue-600 bg-blue-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-blue-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center ${
                    activeStep === index ? 'bg-blue-600' : 'bg-gray-100'
                  }`}>
                    <Icon 
                      name={step?.icon} 
                      size={20} 
                      color={activeStep === index ? '#FFFFFF' : '#6B7280'} 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${
                        activeStep === index ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        Step {index + 1}
                      </span>
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
                      {step?.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600">
                      {step?.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:sticky lg:top-24 h-fit">
            <div className="bg-gray-900 rounded-lg shadow-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <span className="text-gray-400 text-xs ml-2">
                    {steps?.[activeStep]?.language}
                  </span>
                </div>
                <Button variant="ghost" size="xs" iconName="Copy">
                  Copy
                </Button>
              </div>
              <div className="p-4 md:p-6 overflow-x-auto">
                <pre className="text-green-400 text-xs md:text-sm">
                  <code>{steps?.[activeStep]?.code}</code>
                </pre>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="var(--color-secondary)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700">
                    <strong>Pro Tip:</strong> Use our interactive API explorer to test endpoints before implementing them in your code.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickStartGuide;